
/**
 * Optimizes an image for thumbnail use:
 * - Converts to WebP format
 * - Standardizes dimensions to 680x383px (16:9 ratio)
 * - Uses 82% quality compression
 * - Implements center-crop logic to fill the 16:9 frame
 * 
 * @param imageFile - The blob or file to optimize
 * @returns Promise resolving to an optimized WebP blob
 */
export const optimizeThumbnail = async (imageFile: Blob): Promise<Blob> => {
  return new Promise((resolve) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Set target dimensions (16:9 ratio)
    const targetWidth = 680;
    const targetHeight = 383;
    
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    
    img.onload = () => {
      // Calculate crop dimensions to fill 16:9 while maintaining aspect
      const imgAspect = img.width / img.height;
      const targetAspect = targetWidth / targetHeight;
      
      let cropWidth, cropHeight, cropX, cropY;
      
      if (imgAspect > targetAspect) {
        // Image is wider - crop sides
        cropHeight = img.height;
        cropWidth = img.height * targetAspect;
        cropX = (img.width - cropWidth) / 2;
        cropY = 0;
      } else {
        // Image is taller - crop top/bottom
        cropWidth = img.width;
        cropHeight = img.width / targetAspect;
        cropX = 0;
        cropY = (img.height - cropHeight) / 2;
      }
      
      // Draw cropped and resized image
      ctx.drawImage(
        img,
        cropX, cropY, cropWidth, cropHeight,  // Source crop
        0, 0, targetWidth, targetHeight       // Destination
      );
      
      // Convert to WebP with 82% quality
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            // Fallback if WebP is not supported
            canvas.toBlob(
              (jpegBlob) => resolve(jpegBlob || new Blob()), 
              'image/jpeg', 
              0.85
            );
          }
        }, 
        'image/webp', 
        0.82
      );
    };
    
    img.src = URL.createObjectURL(imageFile);
  });
};
